import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { attachImageFiles, fromPrepAi, guessSubject, isPrepAiShape } from "../lib/prepaiFormat";
import { ArrowLeft, Check, CircleCheck } from "lucide-react";
import { verifyFile } from "../api/pqx";
import {
  bulkUploadQuestions,
  fetchSubjects,
  fetchTopics,
  fetchSubtopics,
  type Topic,
  type SubTopic,
} from "../api/worker";

interface ParsedQuestion {
  questionText?: string;
  questionType?: string;
  subjectId?: string;
  topicId?: string;
  subTopicId?: string;
  options?: Record<string, string>;
  correctAnswer?: string;
  explanation?: string;
  difficultyLevel?: number;
  examType?: string;
  examYear?: number;
  questionSource?: string;
  status?: string;
  imageUrl?: string;
  passageContent?: string;
  /** PrepAI format only: the engine's relative figure path, awaiting its file. */
  imagePath?: string;
  topicName?: string;
  subtopicName?: string;
  number?: number;
}

interface SubjectInfo {
  id: string;
  name: string;
  /** JAMB, WAEC or BOTH: a file's exam picks between two subjects of one name. */
  exam_type?: string;
}

type ValidationIssue =
  | "missing-questionText"
  | "missing-questionType"
  | "missing-subjectId"
  | "missing-topicId"
  | "missing-subTopicId"
  | "missing-options"
  | "missing-correctAnswer";

function validate(q: ParsedQuestion): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (!q.questionText?.trim()) issues.push("missing-questionText");
  if (!q.questionType) issues.push("missing-questionType");
  if (!q.subjectId) issues.push("missing-subjectId");
  if (!q.topicId) issues.push("missing-topicId");
  if (!q.subTopicId) issues.push("missing-subTopicId");
  const isObjective = ["mcq", "true_false", "image_multiple_mcq", "multiple_mcq"].includes(
    q.questionType ?? "",
  );
  if (isObjective && (!q.options || Object.keys(q.options).length < 2))
    issues.push("missing-options");
  if (isObjective && !q.correctAnswer) issues.push("missing-correctAnswer");
  return issues;
}

const ISSUE_LABELS: Record<string, string> = {
  "missing-questionText": "Question text",
  "missing-questionType": "Type",
  "missing-subjectId": "Subject",
  "missing-topicId": "Topic",
  "missing-subTopicId": "Subtopic",
  "missing-options": "Options",
  "missing-correctAnswer": "Correct answer",
};

const TYPE_LABELS: Record<string, string> = {
  mcq: "MCQ",
  essay: "Essay",
  true_false: "True/False",
  multiple_mcq: "Multi-MCQ",
  image_multiple_mcq: "Image MCQ",
};

/**
 * Bulk upload with a validation + review step.
 *
 * The worker uploads JSON → the system parses it, highlights missing fields
 * (especially topicId and subTopicId), and shows inline selectors so the
 * worker can fill in the gaps before uploading.
 */

/** The original flow: a complete JSON file, reviewed and uploaded in one go. */
export function JsonUpload() {
  const [json, setJson] = useState("");
  const [questions, setQuestions] = useState<ParsedQuestion[] | null>(null);
  const [subjects, setSubjects] = useState<SubjectInfo[]>([]);
  const [topicCache, setTopicCache] = useState<Record<string, Topic[]>>({});
  const [subtopicCache, setSubtopicCache] = useState<Record<string, SubTopic[]>>({});
  const [result, setResult] = useState<{ created: number; skipped: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  /** A PrepAI/engine file waiting for its subject to be chosen. */
  const [pending, setPending] = useState<Record<string, unknown>[] | null>(null);
  const [pendingSubject, setPendingSubject] = useState("");
  const [missingImages, setMissingImages] = useState<string[]>([]);
  const [pendingExam, setPendingExam] = useState<"jamb" | "waec">("jamb");
  const [pendingName, setPendingName] = useState("questions.json");
  const [checking, setChecking] = useState(false);
  const navigate = useNavigate();

  // Pre-select the subject a PrepAI file names, once both have loaded.
  useEffect(() => {
    if (pending && !pendingSubject) {
      const guess = guessSubject(pending, subjects, pendingName);
      if (guess) setPendingSubject(guess);
    }
  }, [pending, subjects, pendingSubject, pendingName]);

  useEffect(() => {
    fetchSubjects()
      .then((r) => setSubjects(r.subjects))
      .catch(() => {});
  }, []);

  const loadTopics = async (subjectId: string) => {
    if (topicCache[subjectId]) return;
    const r = await fetchTopics(subjectId);
    setTopicCache((prev) => ({ ...prev, [subjectId]: r.topics }));
  };

  const loadSubtopics = async (topicId: string) => {
    if (subtopicCache[topicId]) return;
    const r = await fetchSubtopics(topicId);
    setSubtopicCache((prev) => ({ ...prev, [topicId]: r.subtopics }));
  };

  // Step 1: Parse JSON
  const parseJson = () => {
    setError(null);
    setResult(null);
    setQuestions(null);

    let parsed: unknown;
    try {
      parsed = JSON.parse(json);
    } catch {
      setError("Invalid JSON. Check the format and try again.");
      return;
    }

    const arr = Array.isArray(parsed) ? parsed : (parsed as { questions?: unknown[] })?.questions;
    if (!Array.isArray(arr) || arr.length === 0) {
      setError('Expected a JSON array of questions, or { "questions": [...] }.');
      return;
    }

    // The extraction engine's export (snake_case, options as a JSON string)
    // needs a subject before its topics can be matched; ask, then convert.
    if (isPrepAiShape(arr)) {
      setPending(arr as Record<string, unknown>[]);
      const guess = guessSubject(arr as Record<string, unknown>[], subjects, pendingName);
      setPendingSubject(guess ?? "");
      const exam = String((arr[0] as Record<string, unknown>)?.exam_type ?? "").toLowerCase();
      setPendingExam(exam === "waec" ? "waec" : "jamb");
      return;
    }

    setQuestions(arr as ParsedQuestion[]);

    // Pre-load topics for subjects that are present
    const subjectIds = new Set(
      (arr as ParsedQuestion[]).map((q) => q.subjectId).filter(Boolean) as string[],
    );
    for (const sid of subjectIds) void loadTopics(sid);
  };

  const convertPending = async () => {
    if (!pending || !pendingSubject) return;
    const topics = (await fetchTopics(pendingSubject)).topics;
    setTopicCache((prev) => ({ ...prev, [pendingSubject]: topics }));
    const subtopics: Record<string, SubTopic[]> = {};
    await Promise.all(
      topics.map(async (t) => {
        subtopics[t.id] = (await fetchSubtopics(t.id)).subtopics;
      }),
    );
    setSubtopicCache((prev) => ({ ...prev, ...subtopics }));
    const converted = pending.map((item) =>
      fromPrepAi(item, pendingSubject, { subjects, topics, subtopics }),
    );
    setQuestions(converted);
    setMissingImages(converted.filter((q) => q.imagePath && !q.imageUrl).map((q) => q.imagePath!));
    setPending(null);
  };

  /** Send the file to the engine to check and correct; it comes back as a book to review. */
  const checkPending = async () => {
    if (!pending || !pendingSubject) return;
    setChecking(true);
    setError(null);
    try {
      const book = await verifyFile({
        subjectId: pendingSubject,
        examType: pendingExam,
        filename: pendingName,
        questions: pending,
      });
      navigate(`/books/${book.book_id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't send it to the engine.");
    } finally {
      setChecking(false);
    }
  };

  /** Images the JSON names by path (`figures/…png`), dropped in by the reviewer. */
  const attachImages = async (files: FileList | null) => {
    if (!files || !questions) return;
    const read = await Promise.all(
      [...files].map(
        (file) =>
          new Promise<{ name: string; dataUri: string }>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve({ name: file.name, dataUri: String(reader.result) });
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
          }),
      ),
    );
    const { questions: next, missing } = attachImageFiles(questions, read);
    setQuestions(next);
    setMissingImages(missing);
  };

  // Update a question field
  const updateQ = (index: number, field: keyof ParsedQuestion, value: string) => {
    setQuestions((prev) => {
      if (!prev) return prev;
      const next = [...prev];
      const q = { ...next[index]! };
      (q as Record<string, unknown>)[field] = value || undefined;
      next[index] = q;
      return next;
    });
  };

  // Step 2: Upload after review
  const upload = async () => {
    if (!questions) return;
    setUploading(true);
    setError(null);
    setResult(null);

    try {
      const res = await bulkUploadQuestions(questions as Record<string, unknown>[]);
      setResult(res);
      setQuestions(null);
      setMissingImages([]);
      setJson("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const handleFile = (file: File) => {
    setPendingName(file.name.toLowerCase().endsWith(".json") ? file.name : `${file.name}.json`);
    const reader = new FileReader();
    reader.onload = () => {
      setJson(reader.result as string);
      setQuestions(null);
      setResult(null);
    };
    reader.readAsText(file);
  };

  const allIssues = questions?.map(validate) ?? [];
  const hasBlockers = allIssues.some((issues) =>
    issues.some(
      (i) =>
        i === "missing-questionText" || i === "missing-questionType" || i === "missing-subjectId",
    ),
  );
  const hasWarnings = allIssues.some((issues) => issues.length > 0);
  const readyCount = allIssues.filter(
    (issues) =>
      !issues.includes("missing-questionText") &&
      !issues.includes("missing-questionType") &&
      !issues.includes("missing-subjectId"),
  ).length;

  const subjectName = (id: string | undefined) => subjects.find((s) => s.id === id)?.name ?? "";

  return (
    <div>
      <p className="text-[13px] text-text-muted">
        Upload a JSON file, review and fix any issues, then upload.
      </p>

      {pending ? (
        <div className="mt-6 flex flex-col gap-3 rounded-2xl border border-border bg-surface p-5">
          <div>
            <p className="text-[14px] font-semibold">
              PrepAI format detected · {pending.length} questions
            </p>
            <p className="mt-1 text-[13px] text-text-muted">
              This is the extraction engine's export. Pick the subject these questions belong to;
              topics and subtopics are then matched by name, and anything unmatched is left for you
              to choose.
            </p>
          </div>
          <label className="flex flex-col gap-1 text-[12px] text-text-muted sm:w-80">
            Subject
            <select
              value={pendingSubject}
              onChange={(e) => setPendingSubject(e.target.value)}
              className="rounded-lg border border-border bg-bg px-3 py-2 text-[13px] text-text"
            >
              <option value="">Choose a subject…</option>
              {subjects.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[12px] text-text-muted sm:w-80">
            Exam
            <select
              value={pendingExam}
              onChange={(e) => setPendingExam(e.target.value as "jamb" | "waec")}
              className="rounded-lg border border-border bg-bg px-3 py-2 text-[13px] text-text"
            >
              <option value="jamb">JAMB</option>
              <option value="waec">WAEC</option>
            </select>
          </label>
          <p className="text-[12px] text-text-muted">
            <b className="text-text">Check with the engine first</b> for questions nobody has
            checked yet, AI-generated ones for example: it corrects wrong answers, spelling and
            tags, then brings them in to review like a book, with each change shown. Pictures must
            be web links for the engine to fetch them. <b className="text-text">Import as it is</b>{" "}
            brings them straight in.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={!pendingSubject || checking}
              onClick={() => void checkPending()}
              className="rounded-lg bg-accent px-5 py-2 text-[13px] font-semibold text-bg disabled:opacity-50"
            >
              {checking ? "Sending…" : "Check with the engine first"}
            </button>
            <button
              type="button"
              disabled={!pendingSubject || checking}
              onClick={() => void convertPending()}
              className="rounded-lg border border-border px-5 py-2 text-[13px] font-semibold disabled:opacity-50"
            >
              Import as it is
            </button>
            <button
              type="button"
              onClick={() => setPending(null)}
              className="px-4 py-2 text-[13px] text-text-muted"
            >
              Cancel
            </button>
          </div>
          {error ? (
            <p className="rounded-lg bg-negative/10 px-3 py-2 text-[13px] text-negative">{error}</p>
          ) : null}
        </div>
      ) : null}

      {/* Step 1: File/paste */}
      {!questions && !pending ? (
        <>
          <div
            className="mt-6 flex cursor-pointer items-center justify-center rounded-xl border-2 border-dashed border-border p-8 text-center transition-colors hover:border-accent"
            onClick={() => document.getElementById("file-input")?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const file = e.dataTransfer.files[0];
              if (file) handleFile(file);
            }}
          >
            <div>
              <p className="text-[14px] font-semibold">Drop a JSON file here or click to browse</p>
              <p className="mt-1 text-[12px] text-text-muted">.json files only</p>
            </div>
            <input
              id="file-input"
              type="file"
              accept=".json"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFile(file);
              }}
            />
          </div>

          <div className="mt-4">
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
              Or paste JSON
            </label>
            <textarea
              rows={12}
              value={json}
              onChange={(e) => setJson(e.target.value)}
              placeholder='[{"questionText": "...", "questionType": "mcq", "subjectId": "...", ...}]'
              className="w-full resize-y rounded-lg border border-border bg-surface px-4 py-3 font-mono text-[13px] text-text outline-none placeholder:text-text-muted focus:border-accent"
            />
          </div>

          {error ? (
            <p className="mt-4 rounded-lg bg-negative/10 px-3 py-2 text-[13px] text-negative">
              {error}
            </p>
          ) : null}

          {result ? (
            <p className="mt-4 flex items-center gap-2 rounded-lg bg-positive/10 px-3 py-2 text-[13px] text-positive">
              <CircleCheck size={15} aria-hidden="true" /> Uploaded {result.created} questions.
              {result.skipped > 0 ? ` ${result.skipped} duplicates skipped.` : ""}
            </p>
          ) : null}

          <button
            type="button"
            disabled={!json.trim()}
            onClick={parseJson}
            className="mt-4 rounded-lg bg-accent px-6 py-2.5 text-[14px] font-semibold text-bg hover:bg-accent-light disabled:opacity-50"
          >
            Validate & Review
          </button>
        </>
      ) : !questions ? null : (
        /* Step 2: Review table */
        <>
          {/* Summary bar */}
          <div className="mt-6 flex items-center gap-4 rounded-lg border border-border bg-surface px-4 py-3">
            <span className="text-[14px] font-semibold">{questions.length} questions parsed</span>
            {hasWarnings ? (
              <span className="rounded bg-warning/15 px-2 py-0.5 text-[12px] font-semibold text-warning">
                {allIssues.filter((i) => i.length > 0).length} with issues
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded bg-positive/15 px-2 py-0.5 text-[12px] font-semibold text-positive">
                <Check size={13} aria-hidden="true" /> All valid
              </span>
            )}
            <span className="text-[12px] text-text-muted">{readyCount} ready to upload</span>
          </div>

          {missingImages.length ? (
            <div className="mt-4 rounded-xl border border-warning/40 bg-warning/10 p-4 text-[13px]">
              <p className="font-semibold">
                {missingImages.length} question{missingImages.length === 1 ? " refers" : "s refer"}{" "}
                to image files
              </p>
              <p className="mt-1 text-text-muted">
                Select the images from the engine's <code>figures/</code> folder; they are matched
                by file name and stored with the questions. Questions without their image are
                uploaded without one.
              </p>
              <p className="mt-1 truncate font-mono text-[11px] text-text-muted">
                {missingImages.slice(0, 4).join(", ")}
                {missingImages.length > 4 ? "…" : ""}
              </p>
              <label className="mt-3 inline-block cursor-pointer rounded-lg border border-border bg-surface px-4 py-2 text-[13px] font-medium">
                Select image files
                <input
                  type="file"
                  multiple
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="sr-only"
                  onChange={(e) => void attachImages(e.target.files)}
                />
              </label>
            </div>
          ) : null}

          {error ? (
            <p className="mt-4 rounded-lg bg-negative/10 px-3 py-2 text-[13px] text-negative">
              {error}
            </p>
          ) : null}

          {/* Review cards */}
          <div className="mt-4 flex flex-col gap-3">
            {questions.map((q, i) => {
              const issues = allIssues[i] ?? [];
              const criticalIssues = issues.filter(
                (x) =>
                  x === "missing-questionText" ||
                  x === "missing-questionType" ||
                  x === "missing-subjectId",
              );
              const fixableIssues = issues.filter(
                (x) => x === "missing-topicId" || x === "missing-subTopicId",
              );
              const hasCritical = criticalIssues.length > 0;

              return (
                <div
                  key={i}
                  className={`rounded-xl border p-4 ${
                    hasCritical
                      ? "border-negative/40 bg-negative/5"
                      : fixableIssues.length > 0
                        ? "border-warning/40 bg-warning/5"
                        : "border-border bg-surface"
                  }`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="shrink-0 text-[12px] font-bold text-text-muted">
                          #{i + 1}
                        </span>
                        <span className="rounded bg-surface-high px-2 py-0.5 text-[11px] font-semibold">
                          {TYPE_LABELS[q.questionType ?? ""] ?? q.questionType ?? "?"}
                        </span>
                        {subjectName(q.subjectId) ? (
                          <span className="text-[12px] text-text-muted">
                            {subjectName(q.subjectId)}
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-1 truncate text-[13px]">
                        {q.questionText?.slice(0, 120) || (
                          <span className="text-negative">No question text</span>
                        )}
                      </p>
                    </div>

                    {/* Issue badges */}
                    <div className="flex shrink-0 flex-wrap gap-1">
                      {issues.length === 0 ? (
                        <span className="inline-flex items-center gap-1 rounded bg-positive/15 px-2 py-0.5 text-[11px] font-semibold text-positive">
                          <Check size={12} aria-hidden="true" /> Ready
                        </span>
                      ) : null}
                      {criticalIssues.map((issue) => (
                        <span
                          key={issue}
                          className="rounded bg-negative/15 px-2 py-0.5 text-[11px] font-semibold text-negative"
                        >
                          {ISSUE_LABELS[issue]}
                        </span>
                      ))}
                      {fixableIssues.map((issue) => (
                        <span
                          key={issue}
                          className="rounded bg-warning/15 px-2 py-0.5 text-[11px] font-semibold text-warning"
                        >
                          {ISSUE_LABELS[issue]}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Inline fixers for missing topic/subtopic */}
                  {(fixableIssues.length > 0 || !q.topicId || !q.subTopicId) && q.subjectId ? (
                    <div className="mt-3 flex flex-wrap items-end gap-3 border-t border-border/50 pt-3">
                      {/* Topic selector */}
                      <div className="min-w-[180px] flex-1">
                        <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-text-muted">
                          Topic{" "}
                          {!q.topicId ? <span className="text-warning">— required</span> : null}
                        </label>
                        <select
                          value={q.topicId ?? ""}
                          onChange={(e) => {
                            updateQ(i, "topicId", e.target.value);
                            updateQ(i, "subTopicId", "");
                            if (e.target.value) void loadSubtopics(e.target.value);
                          }}
                          onFocus={() => {
                            if (q.subjectId) void loadTopics(q.subjectId);
                          }}
                          className="w-full rounded-lg border border-border bg-bg px-2.5 py-2 text-[13px] text-text outline-none"
                        >
                          <option value="">Select topic…</option>
                          {(topicCache[q.subjectId] ?? []).map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Subtopic selector */}
                      <div className="min-w-[180px] flex-1">
                        <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-text-muted">
                          Subtopic{" "}
                          {!q.subTopicId ? <span className="text-warning">— required</span> : null}
                        </label>
                        <select
                          value={q.subTopicId ?? ""}
                          onChange={(e) => updateQ(i, "subTopicId", e.target.value)}
                          onFocus={() => {
                            if (q.topicId) void loadSubtopics(q.topicId);
                          }}
                          disabled={!q.topicId}
                          className="w-full rounded-lg border border-border bg-bg px-2.5 py-2 text-[13px] text-text outline-none disabled:opacity-40"
                        >
                          <option value="">Select subtopic…</option>
                          {(subtopicCache[q.topicId ?? ""] ?? []).map((st) => (
                            <option key={st.id} value={st.id}>
                              {st.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          {/* Actions */}
          <div className="mt-6 flex items-center gap-3">
            <button
              type="button"
              disabled={uploading || hasBlockers}
              onClick={() => void upload()}
              className="rounded-lg bg-accent px-6 py-2.5 text-[14px] font-semibold text-bg hover:bg-accent-light disabled:opacity-50"
            >
              {uploading ? "Uploading…" : `Upload ${readyCount} Questions`}
            </button>
            <button
              type="button"
              onClick={() => {
                setQuestions(null);
                setError(null);
              }}
              className="rounded-lg border border-border bg-surface px-6 py-2.5 text-[14px] text-text-muted hover:text-text"
            >
              <span className="inline-flex items-center gap-1.5">
                <ArrowLeft size={15} aria-hidden="true" /> Back to editor
              </span>
            </button>
            {hasBlockers ? (
              <p className="text-[12px] text-negative">
                Fix critical issues (red badges) before uploading.
              </p>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
